import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { type FormEvent, useState } from 'react';
import { Button } from '../../../../../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../../../components-v2/Dialog';
import { Field, FieldDescription, FieldLabel } from '../../../../../../components-v2/Field';
import { Input } from '../../../../../../components-v2/Input';
import { TextArea } from '../../../../../../components-v2/TextArea';
import { SPACE } from '../../../../../../components-v2/Theme/theme';
import { FileTextIcon } from '../../../../../../components/svgs/icons/FileTextIcon';
import { MAX_DESCRIPTION_LENGTH } from '../RequestFilesDialog';
import { styleSheet as filesStyleSheet } from '../styles';

const styleSheet = StyleSheet.create({
	form: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[16],
	},
});

interface IProps {
	/** Names of every file being sent, for the list. */
	fileNames: string[];
	onClose: () => void;
	onContinue: (options: Api.ISharedLinkCreationOptions) => void;
	open: boolean;
}

export const SendFilesDialog = ({ fileNames, onClose, onContinue, open }: IProps) => {
	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');

	const reset = () => {
		setTitle('');
		setDescription('');
	};

	const close = () => {
		reset();
		onClose();
	};

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		reset();
		onContinue({ description: description.trim() || undefined, title: title.trim() || undefined });
	};

	return (
		<Dialog open={open} onOpenChange={next => !next && close()}>
			<DialogPopup>
				<form onSubmit={handleSubmit} noValidate>
					<DialogHeader>
						<DialogTitle>Send files</DialogTitle>
						<DialogDescription>They’ll see the title and details on the secure download page.</DialogDescription>
					</DialogHeader>
					<DialogBody>
						<div className={css(styleSheet.form)}>
							<div className={css(filesStyleSheet.fileRows)}>
								{fileNames.map((name, i) => (
									<div key={`${name}-${i}`} className={css(filesStyleSheet.fileRow)}>
										<FileTextIcon width={16} height={16} className={css(filesStyleSheet.fileIcon)} />
										<span className={css(filesStyleSheet.fileRowName)}>{name}</span>
									</div>
								))}
							</div>
							<Field>
								<FieldLabel htmlFor='send-files-title'>Title (optional)</FieldLabel>
								<Input
									autoFocus
									id='send-files-title'
									onChange={e => setTitle(e.target.value)}
									placeholder='e.g. Your signed trust documents'
									value={title}
								/>
							</Field>
							<Field>
								<FieldLabel htmlFor='send-files-description'>Details (optional)</FieldLabel>
								<TextArea
									id='send-files-description'
									maxLength={MAX_DESCRIPTION_LENGTH}
									onChange={e => setDescription(e.target.value)}
									placeholder='Anything they should know about these files'
									rows={4}
									value={description}
								/>
								<FieldDescription>
									{description.length}/{MAX_DESCRIPTION_LENGTH}
								</FieldDescription>
							</Field>
						</div>
					</DialogBody>
					<DialogFooter>
						<Button onClick={close} type='button' variant='secondary'>
							Cancel
						</Button>
						<Button type='submit'>Continue to email</Button>
					</DialogFooter>
				</form>
			</DialogPopup>
		</Dialog>
	);
};
